/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { usageSetSpendCap } from "../funcs/usageSetSpendCap.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SpendCap } from "../models/components/spendcap.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetSpendCapRequest,
  SetSpendCapSecurity,
} from "../models/operations/setspendcap.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetSpendCapMutationVariables = {
  request: SetSpendCapRequest;
  security?: SetSpendCapSecurity | undefined;
  options?: RequestOptions;
};

export type SetSpendCapMutationData = SpendCap;

export type SetSpendCapMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setSpendCap usage
 *
 * @remarks
 * Set the monthly spend cap for one of a PAYG organization's platform-managed inference keys
 */
export function useSetSpendCapMutation(
  options?: MutationHookOptions<
    SetSpendCapMutationData,
    SetSpendCapMutationError,
    SetSpendCapMutationVariables
  >,
): UseMutationResult<
  SetSpendCapMutationData,
  SetSpendCapMutationError,
  SetSpendCapMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetSpendCapMutation(client, options),
    ...options,
  });
}

export function mutationKeySetSpendCap(): MutationKey {
  return ["@gram/client", "usage", "setSpendCap"];
}

export function buildSetSpendCapMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetSpendCapMutationVariables,
  ) => Promise<SetSpendCapMutationData>;
} {
  return {
    mutationKey: mutationKeySetSpendCap(),
    mutationFn: function setSpendCapMutationFn({
      request,
      security,
      options,
    }): Promise<SetSpendCapMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(usageSetSpendCap(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
